<template>
  <div class="p-grid">
    <Listbox
        v-show="layoutMode === 'static' || !hasSelectedPanel"
        v-model="selectedAlarmPanel"
        :filter="true"
        :filterPlaceholder="_t('label_search')"
        :multiple="false"
        :options="alarmPanels"
        dataKey="name"
        listStyle="height:80vh"
        optionLabel="name"
        class="panel-sidebar"
        @change="onAlarmPanelSelect"
    >
      <template #empty>
        {{ _t('label_no_alarm_panel_found') }}
      </template>
      <template #header>
        <div style="padding: 1.5rem;">
          <h3 class="p-m-0" style="line-height: .9">
            <strong>{{ _t('ALARM_PANEL') }}</strong>
            <Button v-if="!isEditable && editPermission" v-tooltip.bottom="_t('label_add_new')"
                    class="p-button-rounded p-button-success p-button-text"
                    icon="pi pi-plus" style="float: right;"
                    @click="initNewAlarmPanelDialog()"
            />
          </h3>
          <MultiSelectableBreadcrumb ref="breadcrumbData"></MultiSelectableBreadcrumb>
        </div>
      </template>
    </Listbox>
    <transition name="slide-fade">
      <Card v-if="hasSelectedPanel"
            :key="isEditable"
            :class="cardLayoutClass">
        <template #title>
          <div class="panel-header">
            <div class="panel-title">
              <div v-if="isEditable">
                <div class="p-inputgroup p-mb-1">
                  <p class="p-inputgroup-addon">Name</p>
                  <InputText v-model="selectedAlarmPanel.name" :placeholder="_t('label_object_name')" style="height: 2rem;"/>
                </div>
                <div class="p-inputgroup p-mb-1">
                  <p class="p-inputgroup-addon">{{ _t('label_description') }}</p>
                  <InputText v-model="selectedAlarmPanel.description" :placeholder="_t('label_description')" style="height: 2rem;"/>
                </div>
                <div class="stripedBg">
                  <OrganizationLink v-model="selectedAlarmPanel" :editMode="true" :required-field="true"/>
                </div>
              </div>
              <div v-else>
                <h3 class="p-m-1 panel-name"><strong>{{ selectedAlarmPanel.name }}</strong>: {{ selectedAlarmPanel.description }}</h3>
                <h6 class="p-m-1"><strong>{{ _t('label_organization') }}</strong> {{ selectedAlarmPanel.orgName }}</h6>
              </div>
            </div>
            <div class="panel-actions">
              <Button v-tooltip.bottom="_t('label_trigger_alarm')"
                      v-show="(selectedButton && (authorities.includes('SEND_WEB_ALARM') || authorities.includes('TRIGGER_AUTOGOING_ALARM')))"
                      :label="_t('label_coming')"
                      class="p-button-danger p-button-outlined p-button-rounded p-p-2"
                      @click="prepareActivate(selectedButton); triggerModalStatus = 'trigger'"/>
              <Button v-tooltip.bottom="_t('label_trigger_as_manual')"
                      v-show="(selectedButton && authorities.includes('MANUAL_CALL'))"
                      :label="_t('label_manual_call')"
                      class="p-button-rounded p-button-success p-button-outlined p-p-2"
                      @click="showAlarmTriggerModal = true"/>
              <Button v-tooltip.bottom="_t('label_full_screen')"
                      :icon="layoutMode === 'overlay' ? 'mdi mdi-monitor-off' : 'mdi mdi-monitor'"
                      class="p-button-rounded p-button-danger p-button-text"
                      @click="setFullScreenMode(layoutMode === 'static' ? 'overlay' : 'static')"/>
              <Button v-if="isEditable && editPermission" v-tooltip.bottom="_t('Cancel')"
                      class="p-button-rounded p-button-danger p-button-text"
                      icon="pi pi-times"
                      @click="cancelEdit"/>
              <Button v-if="isEditable && editPermission" v-tooltip.bottom="_t('Save')"
                      class="p-button-rounded p-button-text p-button-success"
                      icon="pi pi-check" @click="saveAlarm()"/>
              <Button v-if="!isEditable && editPermission" v-tooltip.bottom="_t('Edit')"
                      class="p-button-rounded p-button-text p-button-secondary"
                      icon="pi pi-pencil" @click="editAlarmPanel(true)"/>
              <Button v-if="!isEditable && editPermission" v-tooltip.bottom="_t('Delete')"
                      class="p-button-rounded p-button-danger p-button-text"
                      icon="pi pi-trash" @click="deleteAlarmPanelDialog = true"/>
            </div>
          </div>
        </template>
        <template #content>
          <div class="tab-bar">
            <div v-for="(row, index) in selectedAlarmPanel.rows" :key="row.id" class="tab-item">
              <div :class="['tab-inner', activeButtonsTab == index ? 'tab-active' : '']">
                <InputText v-if="isEditable" :id="`tab${index}`" v-model="row.text" placeholder="Tab name"
                           class="tab-input"
                           @focus="activeButtonsTab = index; getAlarmPoint(null)"/>
                <span v-else class="tab-label" @click="activeButtonsTab = index; getAlarmPoint(null)">{{ row.text }}</span>
                <Button v-if="isEditable" v-tooltip.bottom="_t('label_delete_operation')"
                        class="p-button-rounded p-button-danger p-button-text"
                        icon="pi pi-times"
                        @click="getAlarmPoint(null); deleteAlarmPanelButton(selectedAlarmPanel.rows, selectedAlarmPanel.rows.indexOf(row), 'tab')"
                />
              </div>
            </div>
            <div class="tab-item">
              <div :class="['tab-inner', activeButtonsTab === 'alarmtable' ? 'tab-active' : '']">
                <span class="tab-label" @click="activeButtonsTab = 'alarmtable'">{{ _t('menu.alarm_table') }}</span>
              </div>
            </div>
            <div v-if="isEditable" class="tab-add">
              <Button v-tooltip.bottom="_t('label_add_new')" class="p-button-success" icon="pi pi-plus"
                      type="button" @click="getAlarmPoint(null); createNewTab()"/>
            </div>
          </div>
          <template v-if="activeButtonsTab !== 'alarmtable'">
          <TabView
                   v-show="!panelTransitioning"
                   v-model:activeIndex="activeButtonsTab"
                   :scrollable="true"
                   class="withCustomTabs hiddenTabs"
                   @tab-click="selectedAlarmPoint.id = null">
            <TabPanel v-for="row in selectedAlarmPanel.rows" :key="row.id">
              <div v-if="row.buttons.length" class="buttons-panel">
                <div class="buttons-container">
                  <draggable :list="row.buttons"
                             class="draggable-list"
                             item-key="id">
                    <template #item="{element: button}">
                      <div class="button-item-wrap">
                        <AlarmButtonEditCard
                            v-if="isEditable"
                            :button="button"
                            :is-selected="!!(selectedAlarmPoint && selectedAlarmPoint.id && selectedAlarmPoint.id === button.apId)"
                            :alarm-options="alarmPointsListPerButton[button.id]"
                            :alarm-search="alarmSearchPerButton[button.id] || ''"
                            :active-size="checkButtonSizeByStyle(button.style)"
                            @icon-click="initIconDialog(selectedAlarmPanel.rows.indexOf(row), row.buttons.indexOf(button))"
                            @color-click="initColorDialog(selectedAlarmPanel.rows.indexOf(row), row.buttons.indexOf(button))"
                            @size-change="button.style = $event"
                            @delete="deleteAlarmPanelButton(row.buttons, row.buttons.indexOf(button), 'button')"
                            @cancel="getAlarmPanel(selectedAlarmPanel.id)"
                            @view="viewButton(button)"
                            @alarm-select="selectAlarmPointIdForButton(button)"
                            @update:alarm-search="alarmSearchPerButton[button.id] = $event; debouncedFilter($event, button.id, button.alarmid, button.alarmName)"
                        />
                        <AlarmButtonViewCard
                            v-else
                            :button="button"
                            :is-point-selected="!!(selectedAlarmPoint && selectedAlarmPoint.id && selectedAlarmPoint.id === button.apId)"
                            :is-button-selected="!!(selectedButton && selectedButton.id === button.id)"
                            @click="onButtonClick($event, button)"
                        />
                      </div>
                    </template>
                  </draggable>
                  <Button v-if="isEditable && row.buttons.length" :title="_t('label_add_new')" icon="pi pi-plus"
                          style="margin-right: .5em"
                          class="p-button-success"
                          type="button" @click="addNewButton(row.buttons)"/>
                </div>
              </div>
              <div v-else-if="row.buttons && row.buttons.length > 0">
                <AlarmButtonEditCard
                    v-if="isEditable"
                    :button="row.buttons[0]"
                    :is-selected="!!(selectedAlarmPoint && selectedAlarmPoint.id && selectedAlarmPoint.id === row.buttons[0].apId)"
                    :alarm-options="alarmPointsListPerButton[row.buttons[0].id]"
                    :alarm-search="alarmSearchPerButton[row.buttons[0].id] || ''"
                    :active-size="checkButtonSizeByStyle(row.buttons[0].style)"
                    @icon-click="initIconDialog(selectedAlarmPanel.rows.indexOf(row), row.buttons.indexOf(row.buttons[0]))"
                    @color-click="initColorDialog(selectedAlarmPanel.rows.indexOf(row), row.buttons.indexOf(row.buttons[0]))"
                    @size-change="row.buttons[0].style = $event"
                    @delete="deleteAlarmPanelButton(row.buttons, row.buttons.indexOf(row.buttons[0]), 'button')"
                    @cancel="getAlarmPanel(selectedAlarmPanel.id)"
                    @view="viewButton(row.buttons[0])"
                    @alarm-select="selectAlarmPointIdForButton(row.buttons[0])"
                    @update:alarm-search="alarmSearchPerButton[row.buttons[0].id] = $event; debouncedFilter($event, row.buttons[0].id, row.buttons[0].alarmid, row.buttons[0].alarmName)"
                />
                <AlarmButtonViewCard
                    v-else
                    :button="row.buttons[0]"
                    :is-point-selected="!!(selectedAlarmPoint && selectedAlarmPoint.id && selectedAlarmPoint.id === row.buttons[0].apId)"
                    :is-button-selected="!!(selectedButton && selectedButton.id === row.buttons[0].id)"
                    @click="onButtonClick($event, row.buttons[0])"
                />
              </div>
            </TabPanel>
          </TabView>
          </template>
          <TabView v-else class="withCustomTabs hiddenTabs alarm-table-tab">
            <TabPanel>
              <AlarmTable :isTitleVisible="false" :isFirstSelected="true" :org-filter="orgFilterForTable" />
            </TabPanel>
          </TabView>
        </template>

      </Card>
    </transition>
    <div :class="detailLayoutClass" class="detail-panel">
      <div v-if="lastJobId && activeButtonsTab !== 'alarmtable'" class="detail-history">
        <history-singe-item
            ref="singleItemData"
            v-model:selected-job-id="lastJobId"
            :hasTriggerProps="true"
            alarm-type="0"
            :fixBrokenJobId="true"
            @on-trigger="prepareActivate(selectedButton)"
        >
        </history-singe-item>
      </div>
      <div :class="!lastJobId ? 'detail-item-flush' : ''"  >
        <AlarmPointItem :id="selectedAlarmPoint && selectedAlarmPoint.id"
                        :hasActions="false"
                        :item="selectedAlarmPoint"
        />
      </div>
    </div>
  </div>

  <CreateAlarmPanelDialog
      v-model="newAlarmPanelDialog"
      v-model:panel="selectedAlarmPanel"
      @save="createAlarmPanel()"
      @cancel="newAlarmPanelDialog = false; selectedAlarmPanel = initAlarmPanel"
  />

  <DeleteAlarmPanelDialog
      v-model="deleteAlarmPanelDialog"
      :panel-name="selectedAlarmPanel && selectedAlarmPanel.name"
      @confirm="deleteAlarmPanel()"
      @cancel="deleteAlarmPanelDialog = false"
  />

  <IconPickerDialog
      v-if="selectedRowIndex !== null && selectedButtonIndex !== null"
      v-model="selectIconDialog"
      :button="selectedAlarmPanel.rows[selectedRowIndex].buttons[selectedButtonIndex]"
      :icon-colors="iconColors"
      :icons="icons"
      :is-editable="isEditable"
  />

  <ColorPickerDialog
      v-if="selectedRowIndex !== null && selectedButtonIndex !== null"
      v-model="selectBackgroundColorDialog"
      :button="selectedAlarmPanel.rows[selectedRowIndex].buttons[selectedButtonIndex]"
      :icon-colors="iconColors"
      :is-editable="isEditable"
  />

  <AlarmModal ref="modal"
              :alarmDialogTextArray="computedAlarmDialogTextArray"
              :data="preparedTriggerBody"
              :isAlarmPanel="true"
              :isOpen="triggerAgainDlg"
              :originalAlarmDialogText="originalAlarmDialogText"
              :replacedAlarmDialogText="replacedAlarmDialogText"
              :triggerModalStatus="triggerModalStatus"
              @onClose="cancelTriggerAlarm"
              @onSubmit="activate"
              @replaceAlarmDialogText="replacedAlarmDialogText = $event"/>

  <AlarmTriggerDialog
      v-model="showAlarmTriggerModal"
      :selected-alarm-point-id="selectedButton && selectedButton.apId"
  />

  <Toast/>
</template>
<script>
import {activateAlarmPanel, createAlarmPanel, deleteAlarmPanelById, getAlarmPanel, getAlarmPanelById, getAlarmPanels, updateAlarmPanel} from '@/api/alarmPanel'
import {showOrg} from '@/api/organization'
import {getAlarmPoint, updateAlarmPoint} from '@/api/alarmPoint'
import {getListAlarmPoint} from '@/api/list'
import OrganizationLink from '@/components/ixarma/OrganizationLink'
import MultiSelectableBreadcrumb from '@/components/ixarma/MultiSelectableBreadcrumb'
import AlarmModal from '@/components/AlarmModal.vue'
import AlarmPointItem from '@/views/alarm_point/item.vue'
import AlarmTable from '@/views/alarm_table/index.vue'
import item from '@/views/alarm_table/alarms/item'
import draggable from 'vuedraggable'
import {mapActions, mapGetters} from 'vuex'
import {toRaw} from 'vue'
import router from '@/router'
import debounce from 'lodash/debounce'
import AlarmButtonEditCard from './components/AlarmButtonEditCard.vue'
import AlarmButtonViewCard from './components/AlarmButtonViewCard.vue'
import CreateAlarmPanelDialog from './components/CreateAlarmPanelDialog.vue'
import DeleteAlarmPanelDialog from './components/DeleteAlarmPanelDialog.vue'
import IconPickerDialog from './components/IconPickerDialog.vue'
import ColorPickerDialog from './components/ColorPickerDialog.vue'
import AlarmTriggerDialog from './components/AlarmTriggerDialog.vue'
import { ICON_COLORS, getIconSections } from './constants/buttonIcons'
import { getPanelsPath as _getPanelsPath, savePath as _savePath } from './utils/panelsPath'

export default {
  name: 'index',
  components: {
    AlarmPointItem,
    AlarmModal,
    'MultiSelectableBreadcrumb': MultiSelectableBreadcrumb,
    OrganizationLink,
    'historySingeItem': item,
    draggable,
    AlarmTable,
    AlarmButtonEditCard,
    AlarmButtonViewCard,
    CreateAlarmPanelDialog,
    DeleteAlarmPanelDialog,
    IconPickerDialog,
    ColorPickerDialog,
    AlarmTriggerDialog,
  },
  data() {
    return {
      lastJobId: null,
      panelTransitioning: false,
      alarmPoints: null,
      alarmPointsListPerButton: {},
      alarmSearchPerButton: {},
      alarmPanels: null,
      initAlarmPoint: {
        id: null,
        icon: {
          name: null,
          color: null
        }
      },
      selectedAlarmPoint: null,
      isEditable: false,
      deleteAlarmPanelDialog: false,
      newAlarmPanelDialog: false,
      selectBackgroundColorDialog: false,
      selectedRowIndex: null,
      selectedButtonIndex: null,
      selectIconDialog: false,
      initAlarmPanel: {
        name: null,
        description: null,
        orgId: localStorage.getItem('orgId'),
        rows: []
      },
      selectedAlarmPanel: {
        id: null,
        name: null,
        description: null,
        orgName: null,
        rows: []
      },
      triggerAgainDlg: false,
      preparedTriggerBody: {
        "alarmText": "",
        "button": {},
        "instructionFiles": [],
        "tempAlarmPoint": null,
        "priority": 1
      },
      orgId: null,
      checkOrgIdIntervalId: null,
      buttonStyleArray: [],
      iconColors: ICON_COLORS,
      icons: [],
      activeButtonsTab: null,
      editPermission: false,
      originalAlarmDialogText: '',
      computedAlarmDialogTextArray: [],
      replacedAlarmDialogText: '',
      initAlarmPanelId: '',
      selectedButton: null,
      isAlarmPanelsNeedRefresh: false,
      alarmPanelsRefreshCounter: 0,
      selectedAlarmPanelRefreshId: null,
      showAlarmTriggerModal: false,
      triggerModalStatus: 'trigger',
      authorities: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).authorities : [],
      debouncedFilter: null,
    }
  },
  created() {
    this.debouncedFilter = debounce((value, id = null, alarmid = null, alarmName = null) => {
      const selectedObject = alarmid ? { alarmPointId: alarmid, name: alarmName ?? '' } : null
      this.getAlarmPoints(value, id, selectedObject)
    }, 500)
    this.selectedAlarmPoint = this.initAlarmPoint
  },
  mounted() {
    this.buttonStyleArray = [
      {
        name: this._t('label_Small'),
        key: 'small',
        value: 'display:inline-block; width:10rem; height:10rem;'
      },
      {
        name: this._t('label_Medium'),
        key: 'medium',
        value: 'display:inline-block; width:12rem; height:12rem;'
      },
      {
        name: this._t('label_Large'),
        key: 'large',
        value: 'display:inline-block; width:16rem; height:16rem;'
      }
    ]
    this.icons = getIconSections(this._t)
    this.editPermission = this.authorities.indexOf("ALARMPANEL_EDIT") !== -1
    this.getAlarmPoints();
    this.checkOrgId();

    const savedPath = _getPanelsPath()

    if (savedPath && Object.keys(savedPath).length) {
      if (savedPath.panel) {
        this.getAlarmPanelByName(savedPath.panel)
      }
    }

    setTimeout(() => {
      if (this.$route.query.panelId) {
        this.getAlarmPanel(this.$route.query.panelId)
      }
      if (this.$route.query.fullscreen) {
        this.setFullScreenMode(this.$route.query.fullscreen === 'true' ? 'overlay' : 'static')
      } else {
        this.changeLayoutModeAsync('static')
      }
    }, 300)

  },
  watch: {
    selectedAlarmPanel(panel) {
      if (localStorage.getItem('panelsPath')) {
        const path = _getPanelsPath()
        const storageTabId = path.tab ?? null;
        const storageButtonId = path.button ?? null;
        const storagePointId = path['point'] ?? null;

        if (panel.rows?.length && storageTabId && storageButtonId) {
          this.setLastJobId({}, panel.rows[storageTabId].buttons.find((el) => el.id === storageButtonId))
        }

        if (panel.rows?.length && storageTabId && storageButtonId && storagePointId) {
          this.getAlarmPoint(storagePointId, panel.rows[storageTabId].buttons.find((el) => el.id === storageButtonId))
        }
      }
    },
    activeButtonsTab(v) {
      if (v === 'alarmtable') {
        this.lastJobId = null
        setTimeout(() => {
          if (this.$refs.singleItemData) {
            this.$refs.singleItemData.stopGetAlarm()
          }
        }, 500)
      }
      const path = _getPanelsPath()
      if (v !== path.tab) {
        _savePath('tab', v, ['button', 'point'])
      } else {
        _savePath('tab', v, [!path.button && 'button', !path['point'] && 'point']);
      }
    },
    selectIconDialog(value) {
      if (value === false) {
        this.selectedRowIndex = null
        this.selectedButtonIndex = null
      }
    },
    replacedAlarmDialogText(value) {
      this.preparedTriggerBody.alarmText = value
    },
    isEditable(value) {
      if (value && this.alarmPoints) {
        const newEntries = {}
        for (const row of this.selectedAlarmPanel.rows) {
          if (row.buttons) {
            for (const button of row.buttons) {
              if (button.id) {
                const list = this.alarmPoints
                const alreadyInList = !button.alarmid || list.some(a => a.alarmPointId === button.alarmid)
                newEntries[button.id] = alreadyInList
                  ? list
                  : [...list, { alarmPointId: button.alarmid, name: button.alarmName ?? '' }]
              }
            }
          }
        }
        this.alarmPointsListPerButton = { ...this.alarmPointsListPerButton, ...newEntries }
      }
    }
  },
  beforeUnmount() {
    clearInterval(parseInt(this.checkOrgIdIntervalId))
    clearInterval(parseInt(this.selectedAlarmPanelRefreshId))
    this.debouncedFilter.cancel()
  },
  methods: {
    ...mapActions({
      'callAlarmPointCalendar': 'alarmpoints/callCalendar',
      'changeLayoutModeAsync': "changeLayoutModeAsync",
    }),
    onAlarmPanelSelect(event) {
      const id = event.value.id
      const name = event.value.name
      this.flushAndRun(() => {
        this.isEditable = false
        this.getAlarmPanel(id)
        _savePath('panel', name, ['button', 'tab', 'point'])
      })
    },
    cancelEdit() {
      const id = this.selectedAlarmPanel.id
      const name = this.selectedAlarmPanel.name
      this.flushAndRun(() => {
        this.editAlarmPanel(false)
        this.selectedAlarmPoint.id = null
        this.getAlarmPanel(id)
        _savePath('panel', name, ['button', 'tab', 'point'])
      })
    },
    onButtonClick(event, button) {
      this.setLastJobId(event, button)
      _savePath('button', button.id, [])
    },
    viewButton(button) {
      this.getAlarmPoint(button.apId, button)
      _savePath('button', button.id, [])
      _savePath('point', button.apId, [])
    },
    addNewButton(buttons) {
      const tempId = '_tmp_' + Date.now()
      buttons.push({
        id: tempId,
        alarmName: '',
        alarmid: '',
        textColor: '#314C61',
        icon: {name: 'mdi mdi-alert-outline'},
        bgColor: '#ffffff'
      })
      this.alarmPointsListPerButton = { ...this.alarmPointsListPerButton, [tempId]: this.alarmPoints || [] }
    },
    flushAndRun(callback) {
      this.debouncedFilter.cancel()
      this.panelTransitioning = true
      this.$nextTick(() => {
        callback()
        this.$nextTick(() => {
          this.panelTransitioning = false
        })
      })
    },
    editAlarmPanel(value) {
      clearInterval(parseInt(this.selectedAlarmPanelRefreshId))
      this.isEditable = value
      if (value === true) {
        this.initAlarmPanelId = this.selectedAlarmPanel.id
      }
    },
    checkOrgId() {
      this.checkOrgIdIntervalId = setInterval(() => {
        if (this.alarmPanelsRefreshCounter > 100) {
          this.isAlarmPanelsNeedRefresh = true
          this.alarmPanelsRefreshCounter = 0;
        }

        if (this.orgId !== localStorage.getItem('orgId') || this.isAlarmPanelsNeedRefresh === true) {
          this.orgId = localStorage.getItem('orgId')
          this.getAlarmPanels();
          this.isAlarmPanelsNeedRefresh = false
        }
        this.alarmPanelsRefreshCounter++;
      }, 100);
    },
    getAlarmPanels() {
      getAlarmPanels(this.orgId).then((response) => {
        this.alarmPanels = response.data.sort((a, b) => a.name.localeCompare(b.name))
      })
    },
    setLastJobId(event, button) {
      this.selectedButton = button
      this.lastJobId = button?.lastjobid ? button?.lastjobid.toString() : null
      event.preventDefault && event.preventDefault()
    },
    getAlarmPanel(id, tab = 0) {
      this.activeButtonsTab = tab
      this.selectedAlarmPoint = this.initAlarmPoint
      router.push({ path: '/alarm-panels', query: { panelId: id, fullscreen:  (this.layoutMode === 'static' ? 'false' : 'true')}})

      if (id !== null && this.newAlarmPanelDialog === false) {
        clearInterval(parseInt(this.selectedAlarmPanelRefreshId))
        this.selectedAlarmPanelRefreshId = null
        this.getAlarmPanelProcessById(id)
        if (!this.isEditable) {
          this.selectedAlarmPanelRefreshId = setInterval(() => {
            this.getAlarmPanelProcessById(id)
          }, 10000)
        }
      } else {
        this.selectedAlarmPanel = this.initAlarmPanel
      }
    },
    getAlarmPanelByName(name, tab = 0) {
      this.activeButtonsTab = tab
      this.selectedAlarmPoint = this.initAlarmPoint

      if (name !== null && this.newAlarmPanelDialog === false) {
        this.getAlarmPanelProcess(name)
      } else {
        this.selectedAlarmPanel = this.initAlarmPanel
      }
    },
    normalizeAlarmPanelRows(panel) {
      if (!panel.rows?.length) return
      for (let row of panel.rows) {
        if (row.buttons === null) {
          row.buttons = [{ bgColor: '#ffffff', textColor: '#314C61', icon: { name: 'mdi mdi-alert-outline' } }]
        }
        for (let button of row.buttons) {
          if (button.style === null) button.style = this.buttonStyleArray[0].value
          if (typeof button.icon === 'string') button.icon = JSON.parse(button.icon)
          if (button.bgColor === null) button.bgColor = '#ffffff'
          if (button.textColor === null) button.textColor = '#314C61'
          if (button.apId != null) button.apId = String(button.apId)
          if (this.selectedButton
              && button.alarmid === this.selectedButton.alarmid
              && button.id === this.selectedButton.id
              && button['custom-name'] == this.selectedButton['custom-name']
          ) {
            this.selectedButton.lastjobid = button?.lastjobid
            this.lastJobId = button?.lastjobid
          }
        }
      }
    },
    applyPanelResponse(data) {
      this.selectedAlarmPanel = data
      this.normalizeAlarmPanelRows(this.selectedAlarmPanel)
      if (this.selectedAlarmPanel.orgId !== null) {
        showOrg(this.selectedAlarmPanel.orgId).then((response) => {
          this.selectedAlarmPanel.orgName = response.data.name
        })
      }
    },
    afterPanelLoad() {
      const savedTab = _getPanelsPath().tab
      if (savedTab) {
        this.activeButtonsTab = this.selectedAlarmPanel.rows.length - 1 < savedTab ? 0 : savedTab
      }
    },
    onPanelLoadError() {
      this.selectedAlarmPanel = this.initAlarmPanel
      this.changeLayoutModeAsync('static')
      this.$toast.add({ severity: 'error', summary: this._t('label_error'), detail: 'Panel not found', life: 3000, closable: false })
    },
    getAlarmPanelProcess(name) {
      getAlarmPanel(name)
          .then((response) => this.applyPanelResponse(response.data))
          .then(() => this.afterPanelLoad())
          .catch(() => this.onPanelLoadError())
    },
    getAlarmPanelProcessById(id) {
      getAlarmPanelById(id)
          .then((response) => this.applyPanelResponse(response.data))
          .then(() => this.afterPanelLoad())
          .catch(() => this.onPanelLoadError())
    },
    async selectAlarmPointIdForButton(button) {
      const match = this.alarmPoints?.find(element => element.alarmPointId === button.alarmid)
      if (!match) return
      let id = match.id
      await this.getAlarmPoint(id)
      button.apId = this.selectedAlarmPoint.id != null ? String(this.selectedAlarmPoint.id) : null
      button.systemId = this.selectedAlarmPoint.systemId
      this.getAlarmPoints()
      return id
    },
    async getAlarmPoint(id, button = null) {
      if (id === null || id === 0) {
        this.selectedAlarmPoint = this.initAlarmPoint
        return
      }
      await getAlarmPoint(id).then((response) => {
        this.selectedAlarmPoint = response.data
        if (this.selectedAlarmPoint?.id != null) {
          this.selectedAlarmPoint.id = String(this.selectedAlarmPoint.id)
        }
        this.lastJobId = button?.lastjobid
        this.selectedButton = button
        this.callAlarmPointCalendar(this.selectedAlarmPoint.id)

        if (this.selectedAlarmPoint) {
          try {
            this.selectedAlarmPoint.icon = JSON.parse(this.selectedAlarmPoint.icon)
          } catch {
            // icon already an object or invalid
          }
          if (!this.selectedAlarmPoint.icon || typeof this.selectedAlarmPoint.icon !== 'object') {
            this.selectedAlarmPoint.icon = { color: '#989898', name: 'mdi mdi-alert-outline' }
          }
        }
      })

    },
    deleteAlarmPanelButton(buttons, index, type = 'button') {
      this.$confirm.require({
        message: type === 'tab' ?
            "Are you sure you would like to delete this tab?\nAll alarm buttons will be also deleted." :
            this._t('label_confirm_deletion'),
        header: this._t('label_Confirmation'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          buttons.splice(index, 1)
        }
      });
    },
    deleteAlarmPanel(id = '', showAlarmPanel = false) {
      if (id === '') {
        id = this.selectedAlarmPanel.id
      }

      deleteAlarmPanelById(id).then(() => {
        clearInterval(parseInt(this.selectedAlarmPanelRefreshId))
        localStorage.removeItem('panelsPath')
        if (!showAlarmPanel) {
          this.selectedAlarmPanel.name = null
        }
        this.getAlarmPanels();
        this.deleteAlarmPanelDialog = false
      })
    },
    initNewAlarmPanelDialog() {
      clearInterval(parseInt(this.selectedAlarmPanelRefreshId))
      this.selectedAlarmPanel = {
        "actionButton": {
          "bottom": true,
          "reset": "",
          "trigger": "",
          "unlock": ""
        },
        "allowedUsers": [],
        "backGroundColor": "",
        "description": "",
        "id": null,
        "fontSize": 10,
        "name": "",
        "orgId": localStorage.getItem('orgId'),
        "pdfFile": {
          "file": "",
          "title": ""
        },
        "returnButton": true,
        "rows": [
          {
            "buttons": [
              {
                "custom-name": "",
                "alarmName": "",
                "alarmid": "",
                "bgColor": "",
                "existable": true,
                "id": "",
                "textColor": ""
              }
            ],
            "text": ""
          }
        ],
        "separator": true
      }
      this.newAlarmPanelDialog = true
    },
    createAlarmPanel() {
      createAlarmPanel(this.selectedAlarmPanel).then(() => {
        this.newAlarmPanelDialog = false
        this.getAlarmPanels()
        this.getAlarmPanelByName(this.selectedAlarmPanel.name)
      })
    },
    saveAlarm() {
      this.debouncedFilter.cancel()
      const payload = JSON.parse(JSON.stringify(this.selectedAlarmPanel))
      for (let row of payload.rows) {
        for (let button of row.buttons) {
          if (typeof button.id === 'string' && button.id.startsWith('_tmp_')) {
            button.id = ''
          }
          if (typeof button.icon !== 'string') {
            button.icon = JSON.stringify({...button.icon, color: button.textColor})
          }
        }
      }
      updateAlarmPanel(payload).then(() => {
        if (this.initAlarmPanelId.length > 0 && this.selectedAlarmPanel.id !== this.initAlarmPanelId) {
          this.deleteAlarmPanel(this.initAlarmPanelId, true)
        }
        const id = this.selectedAlarmPanel.id
        const tab = this.activeButtonsTab
        this.flushAndRun(() => {
          this.isEditable = false
          this.getAlarmPanel(id, tab)
        })
      })

    },
    async getAlarmPoints($filter = '', id = null, selectedObject = null) {
      const listFilters = { alarmPointId: $filter, name: $filter }
      await getListAlarmPoint({"alarmPointMultiFilter": listFilters}, 'ALARMPOINT', 0, '', 10000, this.orgId).then((response) => {
        const list = response.data.list
        if (id) {
          const raw = selectedObject ? toRaw(selectedObject) : null
          const alreadyInList = raw && list.some(i => i.alarmPointId === raw.alarmPointId)
          const extras = raw && !alreadyInList ? [raw] : []
          this.alarmPointsListPerButton = { ...this.alarmPointsListPerButton, [id]: [...list, ...extras] }
        } else {
          this.alarmPoints = list
        }
      })
    },
    initColorDialog(rowIndex, buttonIndex) {
      this.selectBackgroundColorDialog = true
      this.selectedRowIndex = rowIndex
      this.selectedButtonIndex = buttonIndex
    },
    initIconDialog(rowIndex, buttonIndex) {
      this.selectIconDialog = true
      this.selectedRowIndex = rowIndex
      this.selectedButtonIndex = buttonIndex
      if (this.selectedAlarmPanel.rows[rowIndex].buttons[buttonIndex].icon === null) {
        this.selectedAlarmPanel.rows[rowIndex].buttons[buttonIndex].icon = { name: 'mdi mdi-alert-outline', color: '#989898' }
      }
    },
    prepareActivate(button) {
      this.preparedTriggerBody.button.id = button.id
      this.preparedTriggerBody.button.alarmName = button.alarmName
      this.preparedTriggerBody.button.alarmid = button.alarmid
      this.preparedTriggerBody.button['custom-name'] = button['custom-name']
      this.preparedTriggerBody.button.existable = button.existable
      this.preparedTriggerBody.button.icon = button.icon
      this.preparedTriggerBody.button.bgColor = button.bgColor
      this.triggerAgainDlg = true
      this.replacedAlarmDialogText = ''
      if (button.apId) {
        getAlarmPoint(button.apId).then((response) => {
          this.originalAlarmDialogText = response.data.text
          this.computedAlarmDialogTextArray = this.originalAlarmDialogText.split('<original>')
          this.preparedTriggerBody.tempAlarmPoint = response.data
          this.preparedTriggerBody.button.id = response.data.id
          this.preparedTriggerBody.instructionFiles = response.data.instructionFiles ? response.data.instructionFiles : []
        });
      }
    },
    activate() {
      if (this.preparedTriggerBody.tempAlarmPoint && this.preparedTriggerBody.button.id) {
        activateAlarmPanel(this.preparedTriggerBody).then(() => {
          this.triggerAgainDlg = false
        });
        this.preparedTriggerBody.tempAlarmPoint.instructionFiles = this.preparedTriggerBody.instructionFiles
        updateAlarmPoint(this.preparedTriggerBody.tempAlarmPoint)
            .then(() => {
              this.preparedTriggerBody.alarmText = ''
              this.replacedAlarmDialogText = ''
              this.$root.showMessage(this._t('label_alarm_triggered'), 'success')
            })
            .catch((error) => {
              let data = error.response.data ? error.response.data instanceof Object ? error.response.data : JSON.parse(error.response.data) : {}
              this.$root.showMessage((data.reason ? ': ' + data.reason : ''), 'error')
            })
      } else {
        this.$root.showMessage('Trigger alarm parameters of this button are incorrect on the server.', 'warn')
      }
    },
    cancelTriggerAlarm() {
      this.preparedTriggerBody.alarmText = ''
      this.preparedTriggerBody.priority = 1
      this.preparedTriggerBody.button = {}
      this.triggerAgainDlg = false
    },
    createNewTab() {
      const tempId = '_tmp_' + Date.now()
      this.selectedAlarmPanel.rows.push({
        text: 'Tab ' + this.selectedAlarmPanel.rows.length, buttons: [{
          id: tempId,
          ['custom-name']: null,
          appAlarm: false,
        }]
      });
      this.alarmPointsListPerButton = { ...this.alarmPointsListPerButton, [tempId]: this.alarmPoints || [] }
      setTimeout(() => {
        const tabElement = document.getElementById(`tab${this.selectedAlarmPanel.rows.length - 1}`);
        if (tabElement) {
          document.getElementById(`tab${this.selectedAlarmPanel.rows.length - 1}`).focus()
        }
      }, 500)
    },
    setFullScreenMode(mode) {
      this.changeLayoutModeAsync(mode);
      if (this.selectedAlarmPanel?.id) {
        router.push({ path: '/alarm-panels', query: { panelId: this.selectedAlarmPanel.id, fullscreen:  (this.layoutMode === 'static' ? 'false' : 'true')}})
      }
    },
    checkButtonSizeByStyle(style) {
      for (let buttonStyleItem of this.buttonStyleArray) {
        if (buttonStyleItem.value === style) {
          return buttonStyleItem.key
        }
      }

      return 'medium'
    },
  },
  computed: {
    ...mapGetters({
      'layoutMode': "getLayoutMode",
    }),
    hasSelectedPanel() {
      return !!(this.selectedAlarmPanel && this.selectedAlarmPanel.name != null && this.selectedAlarmPanel.id)
    },
    orgFilterForTable() {
      return this.orgId ? [{ id: this.orgId }] : []
    },
    cardLayoutClass() {
      const hasDetail = (this.lastJobId && this.activeButtonsTab !== 'alarmtable') || (this.selectedAlarmPoint && this.selectedAlarmPoint.id)
      const isOverlay = this.layoutMode === 'overlay'
      if (hasDetail) {
        return `style p-col-12 ${isOverlay ? 'p-md-5 p-lg-5' : 'p-md-4 p-lg-4'}`
      }
      return `style p-col-12 ${isOverlay ? 'p-md-11 p-lg-11' : 'p-md-8 p-lg-8'}`
    },
    detailLayoutClass() {
      const hasDetail = (this.lastJobId && this.activeButtonsTab !== 'alarmtable') || (this.selectedAlarmPoint && this.selectedAlarmPoint.id)
      const isOverlay = this.layoutMode === 'overlay'
      if (hasDetail) {
        return `style p-col-12  ${isOverlay ? 'p-md-6 p-lg-6' : 'p-md-4 p-lg-4'}`
      }
      return ''
    },
  }
}
</script>
<style scoped src="./styles/alarm-panel.css" />
<style src="./styles/alarm-panel-global.css" />
